import React, { useCallback, useEffect, useState } from 'react'
import * as XLSX from 'xlsx'
import { formatarMoedaRelatorio, montarRelatorioDesempenho } from '../services/gestaoLeadsRelatorio'

function Kpi({ label, value, hint }) {
  return (
    <div className="gestao-leads-kpi">
      <div className="gestao-leads-kpi-label">{label}</div>
      <div className="gestao-leads-kpi-value">{value}</div>
      {hint ? <div className="gestao-leads-kpi-hint">{hint}</div> : null}
    </div>
  )
}

const GestaoLeadsRelatorio = ({
  show,
  onClose,
  empresaId,
  isPrivilegiado,
  userId,
  usuarios,
  filtroUsuariosIds
}) => {
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [especialistaId, setEspecialistaId] = useState('')
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState('')
  const [relatorio, setRelatorio] = useState(null)

  const carregar = useCallback(async (filtros) => {
    if (!empresaId || !filtros.dataInicio || !filtros.dataFim) return
    setLoading(true)
    setErro('')
    try {
      let usuarioIds = null
      if (!isPrivilegiado) {
        usuarioIds = userId ? [userId] : []
      } else if (filtros.especialistaId) {
        usuarioIds = [filtros.especialistaId]
      }
      const dados = await montarRelatorioDesempenho({
        empresaId,
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim,
        usuarioIds
      })
      setRelatorio(dados)
    } catch (err) {
      console.error(err)
      setErro('Não foi possível montar o relatório. Tente novamente.')
      setRelatorio(null)
    } finally {
      setLoading(false)
    }
  }, [empresaId, isPrivilegiado, userId])

  useEffect(() => {
    if (!show) return undefined
    const now = new Date()
    const y = now.getFullYear()
    const m = String(now.getMonth() + 1).padStart(2, '0')
    const lastDay = new Date(y, now.getMonth() + 1, 0).getDate()
    const ini = `${y}-${m}-01`
    const fim = `${y}-${m}-${String(lastDay).padStart(2, '0')}`
    const unico = Array.isArray(filtroUsuariosIds) && filtroUsuariosIds.length === 1
      ? filtroUsuariosIds[0]
      : ''
    const inicial = {
      dataInicio: ini,
      dataFim: fim,
      especialistaId: isPrivilegiado ? unico : (userId || '')
    }
    setDataInicio(inicial.dataInicio)
    setDataFim(inicial.dataFim)
    setEspecialistaId(inicial.especialistaId)
    carregar(inicial)
  }, [show, carregar, filtroUsuariosIds, isPrivilegiado, userId])

  if (!show) return null

  const handleFiltrar = (e) => {
    e.preventDefault()
    carregar({ dataInicio, dataFim, especialistaId })
  }

  const exportarExcel = () => {
    if (!relatorio) return
    const header = [
      'Especialista',
      'Leads criados',
      'Atendidos',
      'Sem atendimento',
      'Convertidos',
      'Taxa atendimento %',
      'Taxa conversão %',
      'Conversão sobre atendidos %',
      'Valor vendido',
      'Ticket médio',
      'Em pipeline'
    ]
    const toRow = (row, nome) => ([
      nome,
      row.criados,
      row.atendidos,
      row.semAtendimento,
      row.convertidos,
      row.taxaAtendimento,
      row.taxaConversao,
      row.taxaConversaoAtendidos,
      row.valorConvertido,
      row.ticketMedio,
      row.emPipeline
    ])
    const body = relatorio.linhas.map((row) => toRow(row, row.especialistaNome))
    body.push(toRow(relatorio.totais, 'TOTAL'))
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet([header, ...body])
    XLSX.utils.book_append_sheet(wb, ws, 'Desempenho')
    if (relatorio.modo === 'individual' && relatorio.individual?.convertidosDetalhe?.length) {
      const convHeader = ['Cliente', 'Telefone', 'Valor', 'Efetivado em']
      const convRows = relatorio.individual.convertidosDetalhe.map((l) => ([
        l.nome,
        l.telefone,
        Number(l.valor_contrato) || 0,
        l.efetivado_em ? String(l.efetivado_em).slice(0, 10) : ''
      ]))
      const wsConv = XLSX.utils.aoa_to_sheet([convHeader, ...convRows])
      XLSX.utils.book_append_sheet(wb, wsConv, 'Vendas')
    }
    XLSX.writeFile(wb, `relatorio_gestao_leads_${relatorio.dataInicio}_${relatorio.dataFim}.xlsx`)
  }

  const kpis = relatorio?.modo === 'individual' ? relatorio.individual : relatorio?.totais
  const tituloEspecialista = relatorio?.modo === 'individual'
    ? relatorio.individual?.especialistaNome
    : 'Todos os especialistas'

  return (
    <div className="gestao-leads-modal-overlay" onClick={onClose}>
      <div className="gestao-leads-modal xwide" onClick={(e) => e.stopPropagation()}>
        <h2>Relatório de desempenho</h2>
        <p className="gestao-leads-relatorio-sub">
          {isPrivilegiado
            ? 'Com um especialista selecionado o relatório fica individual; sem seleção, agrupa todos.'
            : 'Seu desempenho no período selecionado.'}
        </p>

        <form className="gestao-leads-hist-filtros" onSubmit={handleFiltrar}>
          <div className="gestao-leads-form-group">
            <label>Data início</label>
            <input type="date" required value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
          </div>
          <div className="gestao-leads-form-group">
            <label>Data fim</label>
            <input type="date" required value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
          </div>
          {isPrivilegiado && (
            <div className="gestao-leads-form-group">
              <label>Especialista</label>
              <select value={especialistaId} onChange={(e) => setEspecialistaId(e.target.value)}>
                <option value="">Todos (agrupado)</option>
                {(usuarios || []).map((u) => (
                  <option key={u.id} value={u.id}>{u.nome}</option>
                ))}
              </select>
            </div>
          )}
          <div className="gestao-leads-form-group gestao-leads-hist-filtros-btn">
            <label>&nbsp;</label>
            <button type="submit" className="gestao-leads-btn" disabled={loading}>
              {loading ? 'Carregando…' : 'Gerar'}
            </button>
          </div>
        </form>

        {erro && <div className="gestao-leads-msg error">{erro}</div>}

        {loading && !relatorio ? (
          <p style={{ color: 'var(--gl-text-sub)' }}>Montando relatório…</p>
        ) : relatorio && kpis ? (
          <>
            <p className="gestao-leads-relatorio-periodo">
              {relatorio.dataInicio.split('-').reverse().join('/')} a {relatorio.dataFim.split('-').reverse().join('/')}
              {' · '}
              {tituloEspecialista}
            </p>
            <div className="gestao-leads-kpi-grid">
              <Kpi label="Leads criados" value={kpis.criados} hint="Novos no período" />
              <Kpi label="Atendidos" value={kpis.atendidos} hint={`${kpis.taxaAtendimento}% dos criados`} />
              <Kpi label="Sem atendimento" value={kpis.semAtendimento} hint="Ainda em Novo Lead" />
              <Kpi label="Convertidos" value={kpis.convertidos} hint={`${kpis.taxaConversao}% dos criados`} />
              <Kpi label="Valor vendido" value={formatarMoedaRelatorio(kpis.valorConvertido)} hint={`Ticket ${formatarMoedaRelatorio(kpis.ticketMedio)}`} />
              <Kpi label="Em pipeline" value={kpis.emPipeline} hint="Ativos sem venda efetivada" />
            </div>

            {relatorio.modo === 'agrupado' && (
              <div className="gestao-leads-hist-table-wrap">
                <table className="gestao-leads-hist-table">
                  <thead>
                    <tr>
                      <th>Especialista</th>
                      <th>Criados</th>
                      <th>Atendidos</th>
                      <th>Sem atend.</th>
                      <th>Convertidos</th>
                      <th>Atend. %</th>
                      <th>Conv. %</th>
                      <th>Valor</th>
                      <th>Pipeline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relatorio.linhas.length === 0 ? (
                      <tr>
                        <td colSpan={9}>Nenhum movimento no período.</td>
                      </tr>
                    ) : relatorio.linhas.map((row) => (
                      <tr key={row.especialistaId || 'sem'}>
                        <td>{row.especialistaNome}</td>
                        <td>{row.criados}</td>
                        <td>{row.atendidos}</td>
                        <td>{row.semAtendimento}</td>
                        <td>{row.convertidos}</td>
                        <td>{row.taxaAtendimento}%</td>
                        <td>{row.taxaConversao}%</td>
                        <td>{formatarMoedaRelatorio(row.valorConvertido)}</td>
                        <td>{row.emPipeline}</td>
                      </tr>
                    ))}
                    {relatorio.linhas.length > 0 && (
                      <tr className="gestao-leads-relatorio-total">
                        <td>Total</td>
                        <td>{relatorio.totais.criados}</td>
                        <td>{relatorio.totais.atendidos}</td>
                        <td>{relatorio.totais.semAtendimento}</td>
                        <td>{relatorio.totais.convertidos}</td>
                        <td>{relatorio.totais.taxaAtendimento}%</td>
                        <td>{relatorio.totais.taxaConversao}%</td>
                        <td>{formatarMoedaRelatorio(relatorio.totais.valorConvertido)}</td>
                        <td>{relatorio.totais.emPipeline}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {relatorio.modo === 'individual' && relatorio.individual?.convertidosDetalhe?.length > 0 && (
              <div className="gestao-leads-hist-table-wrap" style={{ marginTop: '1rem' }}>
                <table className="gestao-leads-hist-table">
                  <thead>
                    <tr>
                      <th>Vendas efetivadas</th>
                      <th>Telefone</th>
                      <th>Valor</th>
                      <th>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relatorio.individual.convertidosDetalhe.map((l) => (
                      <tr key={l.id}>
                        <td>{l.nome}</td>
                        <td>{l.telefone || '—'}</td>
                        <td>{formatarMoedaRelatorio(l.valor_contrato)}</td>
                        <td>{l.efetivado_em ? new Date(l.efetivado_em).toLocaleDateString('pt-BR') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null}

        <div className="gestao-leads-modal-actions">
          <button type="button" className="gestao-leads-btn outline" onClick={onClose}>
            Fechar
          </button>
          <button
            type="button"
            className="gestao-leads-btn secondary"
            onClick={exportarExcel}
            disabled={!relatorio || loading}
          >
            Exportar Excel
          </button>
        </div>
      </div>
    </div>
  )
}

export default GestaoLeadsRelatorio
